import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Terms consent gate above the cart's Checkout button (snippets/cart-terms.liquid).
 * The choice is written to the cart attribute `terms_accepted`; the checkbox also posts it with
 * the checkout form, so the order carries it even while that write is still in flight.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The consent checkbox.
 * @property {HTMLElement} error - The error message, `hidden` until Checkout is pressed unchecked.
 *
 * @extends {Component<Refs>}
 */
class CartTermsComponent extends Component {
  requiredRefs = ['input', 'error'];

  connectedCallback() {
    super.connectedCallback();
    instances.add(this);
    // Fresh server markup wins unless a consent write from this page is still on its way.
    if (accepted === null || !isWritePending()) accepted = this.refs.input.checked;
    this.applyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    instances.delete(this);
  }

  /** @param {Event} event */
  updateConsent(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    setAccepted(event.target.checked);
    writeCartAttribute(event.target.checked);
  }

  /** @param {SubmitEvent} event */
  gateCheckout(event) {
    if (this.refs.input.checked) return;
    event.preventDefault();
    this.#setError(true);
    this.refs.input.focus();
  }

  applyState() {
    if (accepted === null) return;
    const { input, error } = this.refs;
    if (input.checked !== accepted) input.checked = accepted;
    this.#setError(!accepted && !error.hidden);
  }

  /** @param {boolean} visible */
  #setError(visible) {
    const { input, error } = this.refs;
    error.hidden = !visible;
    if (visible) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', error.id);
      return;
    }
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }
}

const CART_ATTRIBUTE = 'terms_accepted';
const ACCEPTED_VALUE = 'true';

/** @type {boolean | null} Unknown until the first instance reads its server-rendered state. */
let accepted = null;

/** @type {Set<CartTermsComponent>} */
const instances = new Set();

/** @type {boolean | null} The value waiting to be written, coalesced to the newest. */
let queuedValue = null;
let writeInFlight = false;
let writeChain = Promise.resolve();

function isWritePending() {
  return writeInFlight || queuedValue !== null;
}

/** @param {boolean} value */
function setAccepted(value) {
  accepted = value;
  for (const instance of instances) instance.applyState();
}

/** @param {boolean} value */
function writeCartAttribute(value) {
  queuedValue = value;
  writeChain = writeChain.then(writeQueuedValue);
}

async function writeQueuedValue() {
  if (queuedValue === null) return;
  const value = queuedValue;
  queuedValue = null;
  writeInFlight = true;

  try {
    let response = await postCartAttribute(value);
    if (response.status === 409) response = await postCartAttribute(value);
    if (!response.ok) throw new Error(`cart update failed (${response.status})`);
  } catch (error) {
    console.warn('[cart-terms] consent not saved to the cart:', error);
  } finally {
    writeInFlight = false;
  }
}

/** @param {boolean} value */
function postCartAttribute(value) {
  const body = JSON.stringify({ attributes: { [CART_ATTRIBUTE]: value ? ACCEPTED_VALUE : '' } });
  return fetch(Theme.routes.cart_update_url, { ...fetchConfig('json', { body }), keepalive: true });
}

async function refreshFromCart() {
  if (instances.size === 0 || isWritePending()) return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`cart read failed (${response.status})`);
    const cart = await response.json();
    setAccepted(cart?.attributes?.[CART_ATTRIBUTE] === ACCEPTED_VALUE);
  } catch (error) {
    console.warn('[cart-terms] consent not re-read from the cart:', error);
  }
}

/** @param {Event} event */
function gateCheckoutOnSubmit(event) {
  if (!('submitter' in event)) return;
  const { submitter } = /** @type {SubmitEvent} */ (event);
  const component = submitter?.closest('cart-terms-component');
  if (component instanceof CartTermsComponent) component.gateCheckout(/** @type {SubmitEvent} */ (event));
}

/** @param {import('@shopify/events').CartLinesUpdateEvent} event */
function resetConsentOnEmptyCart(event) {
  event.promise
    ?.then(({ cart, detail }) => {
      if ((cart?.totalQuantity ?? detail?.itemCount) !== 0) return;
      setAccepted(false);
      writeCartAttribute(false);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-terms] Event promise rejected:', error);
    });
}

// A page restored from the back/forward cache, or rebuilt from the disk cache on Back, shows the consent it left with.
/** @param {PageTransitionEvent} event */
function refreshOnRestore(event) {
  const navigation = performance.getEntriesByType('navigation')[0];
  const navigationType = navigation instanceof PerformanceNavigationTiming ? navigation.type : undefined;
  if (!event.persisted && navigationType !== 'back_forward') return;
  refreshFromCart();
}

document.addEventListener('submit', gateCheckoutOnSubmit, { capture: true });
document.addEventListener(StandardEvents.cartLinesUpdate, resetConsentOnEmptyCart);
window.addEventListener('pageshow', refreshOnRestore);

if (!customElements.get('cart-terms-component')) {
  customElements.define('cart-terms-component', CartTermsComponent);
}
